"use client";

import { useEffect, useRef, useState } from "react";
import { HomeQueryParam, TeamModalUrlMode } from "@/constants/navigation.enum";
import {
  TeamModalEditIntent,
  TeamModalMode,
  TeamModalStatus,
  TeamModalSubjectKind,
  useTeamModal,
} from "@/contexts/team-modal";
import { stripAllTeamsGalleryUrl } from "@/hooks/navigation/all-teams-url";
import {
  parseTeamModalUrlMode,
  setTeamModalUrl,
} from "@/hooks/navigation/view-team-url";
import {
  MobileModalDebugBranch,
  MobileModalDebugEvent,
  type PersistentDebugLogger,
  captureMobileModalEnvironment,
} from "@/hooks/telemetry";
import { consumeAuthReturnRestoreRoute } from "@/lib/authReturnRoute";
import type {
  OpenTeamDetailsInput,
  OpenTeamEditorInput,
} from "./domain/home-page/types";

interface UseViewTeamUrlRestoreInput {
  isAuthenticated: boolean;
  loading: boolean;
  modalDebugLogger: PersistentDebugLogger;
  openDeployForTeam: (teamId: string) => void;
  openTeamDetails: (input: OpenTeamDetailsInput) => void;
  openTeamEditor: (input: OpenTeamEditorInput) => void;
}

interface UseHomePageUrlRestoresInput extends UseViewTeamUrlRestoreInput {
  openAllTeamsGallery: () => void;
}

interface LogUrlRestoreInput {
  event: MobileModalDebugEvent;
  modalDebugLogger: PersistentDebugLogger;
  selectedBranch: MobileModalDebugBranch;
  teamId?: string;
}

/** One parsed `?viewTeam=<id>[&mode=…]` occurrence awaiting dispatch. */
interface PendingTeamModalRestore {
  teamId: string;
  mode: TeamModalUrlMode;
}

function getCurrentQueryParam(param: HomeQueryParam): string | null {
  return new URLSearchParams(window.location.search).get(param);
}

/** The same param, read off a restored route instead of the address bar. */
function getRouteQueryParam(route: string, param: HomeQueryParam): string | null {
  const queryIndex = route.indexOf("?");
  if (queryIndex === -1) return null;

  return new URLSearchParams(route.slice(queryIndex + 1)).get(param);
}

function logUrlRestore({
  event,
  modalDebugLogger,
  selectedBranch,
  teamId,
}: LogUrlRestoreInput): void {
  modalDebugLogger.logBranchChoice({
    name: event,
    selectedBranch,
    payload: {
      ...(teamId ? { teamId } : {}),
      environment: captureMobileModalEnvironment(),
    },
  });
}

/**
 * Reopen the team named by `?viewTeam=`, in the state named by `mode`
 * (absent/unknown = the regular view state). The params arrive two ways, and
 * one of them lands on a landing that is already mounted:
 *
 *  - a cold load already carrying them — a shared link, a refresh, the hop
 *    back from `/auth/callback`, a session save's deploy navigation;
 *  - an authenticated return resuming an interrupted Copy, where
 *    `useAuthReturnRouteResume` restores the parked route with a soft
 *    `router.replace`. Nothing remounts, so a gate that only looks once has
 *    already looked.
 *
 * The second trigger reads the route off the restore marker that arms that
 * replace, never off the address bar: the marker is written before the router
 * navigates, which makes the restore observable in the same commit instead of
 * whenever the router gets round to writing the URL. It also keeps this hook
 * deaf to every OTHER way the params change — a card click writes `?viewTeam=`
 * through its own opener and must not be reopened from under itself. The
 * marker is consume-once and TTL-bounded, so the restore cannot repeat or loop.
 *
 * Dispatch timing splits by mode: view opens immediately (it is auth-agnostic),
 * while deploy/edit/copy wait for auth to resolve — deploy gates before
 * opening, and the editor's permission fallback needs the viewer's real
 * session to classify the team against.
 */
function useViewTeamUrlRestore({
  isAuthenticated,
  loading,
  modalDebugLogger,
  openDeployForTeam,
  openTeamDetails,
  openTeamEditor,
}: UseViewTeamUrlRestoreInput): void {
  const hasProcessedViewTeamUrlRef = useRef(false);
  const restoredViewTeamIdRef = useRef<string | null>(null);
  const [pendingRestore, setPendingRestore] =
    useState<PendingTeamModalRestore | null>(null);

  useEffect(() => {
    if (hasProcessedViewTeamUrlRef.current) {
      return;
    }

    hasProcessedViewTeamUrlRef.current = true;
    const initialViewTeamId = getCurrentQueryParam(HomeQueryParam.ViewTeam);

    if (!initialViewTeamId) {
      logUrlRestore({
        event: MobileModalDebugEvent.ViewTeamUrlRestore,
        modalDebugLogger,
        selectedBranch: MobileModalDebugBranch.NoQueryParam,
      });
      return;
    }

    setPendingRestore({
      teamId: initialViewTeamId,
      mode:
        parseTeamModalUrlMode(
          getCurrentQueryParam(HomeQueryParam.TeamModalMode),
        ) ?? TeamModalUrlMode.View,
    });
  }, [modalDebugLogger]);

  useEffect(() => {
    // The marker is armed by exactly one caller — the authenticated branch of
    // `useAuthReturnRouteResume`, which acts in this same commit, ahead of this
    // hook. So the two conditions that gate that branch gate this read: auth
    // resolved, and resolved to a session.
    //
    // `isAuthenticated` is load-bearing, not decoration. Auth resolves ONCE
    // (`lib/authState` sets `loading: false` on the first session snapshot and
    // never back), so every later sign-in flips `isAuthenticated` with
    // `loading` frozen at false — including a session adopted from another
    // tab, which is where a magic link opened from a webmail lands. Keyed on
    // `loading` alone this effect would not re-run, leaving the marker unread,
    // `?viewTeam=` in the address bar and no modal until a reload.
    if (loading || !isAuthenticated) {
      return;
    }

    const restoredRoute = consumeAuthReturnRestoreRoute();
    if (restoredRoute == null) {
      return;
    }

    const restoredViewTeamId = getRouteQueryParam(
      restoredRoute,
      HomeQueryParam.ViewTeam,
    );
    if (!restoredViewTeamId) {
      return;
    }

    setPendingRestore({
      teamId: restoredViewTeamId,
      mode:
        parseTeamModalUrlMode(
          getRouteQueryParam(restoredRoute, HomeQueryParam.TeamModalMode),
        ) ?? TeamModalUrlMode.View,
    });
  }, [isAuthenticated, loading]);

  useEffect(() => {
    if (pendingRestore == null) {
      return;
    }
    // Deploy gates on the resolved session; the editor modes classify the
    // team against it. Only the plain view opens before auth settles.
    if (pendingRestore.mode !== TeamModalUrlMode.View && loading) {
      return;
    }
    if (restoredViewTeamIdRef.current === pendingRestore.teamId) {
      setPendingRestore(null);
      return;
    }

    restoredViewTeamIdRef.current = pendingRestore.teamId;
    setPendingRestore(null);
    const { teamId } = pendingRestore;
    const restoreByMode: Record<TeamModalUrlMode, () => void> = {
      [TeamModalUrlMode.View]: () => {
        logUrlRestore({
          event: MobileModalDebugEvent.ViewTeamUrlRestore,
          modalDebugLogger,
          selectedBranch: MobileModalDebugBranch.OpenTeamDetails,
          teamId,
        });
        // URL restore has no source card — centre grow is correct.
        openTeamDetails({ teamId, morphSource: null });
      },
      [TeamModalUrlMode.Deploy]: () => {
        logUrlRestore({
          event: MobileModalDebugEvent.DeployTeamUrlRestore,
          modalDebugLogger,
          selectedBranch: MobileModalDebugBranch.OpenPreflight,
          teamId,
        });
        openDeployForTeam(teamId);
      },
      [TeamModalUrlMode.Edit]: () => {
        logUrlRestore({
          event: MobileModalDebugEvent.ViewTeamUrlRestore,
          modalDebugLogger,
          selectedBranch: MobileModalDebugBranch.OpenEditPreflight,
          teamId,
        });
        openTeamEditor({ teamId, editIntent: TeamModalEditIntent.Update });
      },
      [TeamModalUrlMode.Copy]: () => {
        logUrlRestore({
          event: MobileModalDebugEvent.ViewTeamUrlRestore,
          modalDebugLogger,
          selectedBranch: MobileModalDebugBranch.StartSaveCopy,
          teamId,
        });
        openTeamEditor({ teamId, editIntent: TeamModalEditIntent.Copy });
      },
    };
    restoreByMode[pendingRestore.mode]();
  }, [
    loading,
    modalDebugLogger,
    openDeployForTeam,
    openTeamDetails,
    openTeamEditor,
    pendingRestore,
  ]);
}

interface UseAllTeamsGalleryUrlRestoreInput {
  openAllTeamsGallery: () => void;
}

/**
 * Open the All Teams gallery when the arrival URL carries the one-shot
 * `?allTeams=` marker — the session-start chooser's "Existing team" option
 * landing from another route. Mount-once like `useViewTeamUrlRestore`'s cold
 * branch, and auth-agnostic like the gallery itself (logged out it shows the
 * CABAL selection, no gate). The param is stripped BEFORE the open fires, so
 * a re-render or a failed open can never replay it. A chooser click made
 * while the landing is mounted never writes this param: it goes through the
 * opener the landing registers on the chooser context, so this hook stays
 * deaf to everything but a real cross-route arrival.
 */
function useAllTeamsGalleryUrlRestore({
  openAllTeamsGallery,
}: UseAllTeamsGalleryUrlRestoreInput): void {
  const hasProcessedAllTeamsUrlRef = useRef(false);

  useEffect(() => {
    if (hasProcessedAllTeamsUrlRef.current) {
      return;
    }

    hasProcessedAllTeamsUrlRef.current = true;
    if (!getCurrentQueryParam(HomeQueryParam.AllTeamsGallery)) {
      return;
    }

    stripAllTeamsGalleryUrl();
    openAllTeamsGallery();
  }, [openAllTeamsGallery]);
}

export function useHomePageUrlRestores(
  input: UseHomePageUrlRestoresInput,
): void {
  useViewTeamUrlRestore(input);
  useAllTeamsGalleryUrlRestore({
    openAllTeamsGallery: input.openAllTeamsGallery,
  });
}

/**
 * The landing's ONE writer of the team-modal URL grammar, reacting to the
 * canonical controller's state: `?viewTeam=<id>` while the modal is open,
 * plus `mode=deploy` while it sits in deploy; both dropped when it closes.
 * The edit modes are restore-only, so the writer never emits them — a
 * restored `mode=edit|copy` link is consumed like the old one-shot deploy
 * param was.
 *
 * Sync acts only on the controller's own transitions (the never-opened latch):
 * the gallery deck writes `?viewTeam=` from its own committed-open
 * bookkeeping, and a continuously-enforced writer would clobber it. The
 * overlay's in-place retargets (linked-copy Show, saved-copies pick, create
 * and save-copy landings) ARE controller transitions — they report the new
 * subject — so this writer names the retargeted team like any other open; the
 * overlay's own `setViewTeamUrl` covers the hosts that have no sync mounted.
 */
export function useTeamModalUrlSync(): void {
  const { state } = useTeamModal();
  const openState = state.status === TeamModalStatus.Open ? state : null;
  const openSubject = openState?.subject ?? null;
  // Creation writes NO url — a New subject has no id to name. When the create
  // lands and the subject swaps to the record, the sync writes it like any
  // other open.
  const teamId =
    openSubject != null && openSubject.kind === TeamModalSubjectKind.Existing
      ? openSubject.teamId
      : null;
  const mode = openState?.modeState.mode ?? null;
  const hasSyncedOpenRef = useRef(false);

  useEffect(() => {
    if (teamId == null) {
      if (!hasSyncedOpenRef.current) return;
      hasSyncedOpenRef.current = false;
      setTeamModalUrl({ teamId: null, mode: null });
      return;
    }
    hasSyncedOpenRef.current = true;
    setTeamModalUrl({
      teamId,
      mode: mode === TeamModalMode.Deploy ? TeamModalUrlMode.Deploy : null,
    });
  }, [teamId, mode]);
}
